// Defines log levels
export enum Level {
  Emergency = 0,
  Alert = 1,
  Critical = 2,
  Error = 3,
  Warning = 4,
  Notice = 5,
  Info = 6,
  Debug = 7
}

/**
 * Helps in logging information.
 *
 * @author Philipp Seiler <sep@earlybird.at>
 */
export class Logger {
  /**
   * Prints a log message, if debugging is enabled.
   *
   * @param {string} context Message context
   * @param {Level} [level] Log level
   * @param {any[]} message Message to log
   * @returns {void}
   */
  public static log(context: string, level: Level = Level.Emergency, ...message: any[]): void {
    if (!A11yConsentConfiguration.settings.settings.debug.enabled) {
      return;
    }
    let targetLevel = Level.Emergency;
    if (A11yConsentConfiguration.settings.settings.debug.level) {
      targetLevel = parseInt(Level[A11yConsentConfiguration.settings.settings.debug.level].valueOf());
    }
    if (targetLevel >= level) {
      switch (level) {
        case Level.Emergency:
          console.log(`%c[${context}]`, 'color: #ff0000; font-weight: bold;', ...message);
          break;
        case Level.Alert:
          console.log(`%c[${context}]`, 'color: #eb0000; font-weight: bold;', ...message);
          break;
        case Level.Critical:
          console.log(`%c[${context}]`, 'color: #d80000; font-weight: bold;', ...message);
          break;
        case Level.Error:
          console.log(`%c[${context}]`, 'color: #c40000; font-weight: bold;', ...message);
          break;
        case Level.Warning:
          console.log(`%c[${context}]`, 'color: #ffa500; font-weight: bold;', ...message);
          break;
        case Level.Notice:
          console.log(`%c[${context}]`, 'color: #1414ff; font-weight: bold;', ...message);
          break;
        case Level.Info:
          console.log(`%c[${context}]`, 'color: #0aff0a; font-weight: bold;', ...message);
          break;
        case Level.Debug:
          console.log(`%c[${context}]`, 'color: #ff76ff; font-weight: bold;', ...message);
          break;
      }
    }
  }

  public static emergency(context: string, ...message: any[]): void {
    Logger.log(context, Level.Emergency, ...message);
  }

  public static alert(context: string, ...message: any[]): void {
    Logger.log(context, Level.Alert, ...message);
  }

  public static critical(context: string, ...message: any[]): void {
    Logger.log(context, Level.Critical, ...message);
  }

  public static error(context: string, ...message: any[]): void {
    Logger.log(context, Level.Error, ...message);
  }

  public static warning(context: string, ...message: any[]): void {
    Logger.log(context, Level.Warning, ...message);
  }

  public static notice(context: string, ...message: any[]): void {
    Logger.log(context, Level.Notice, ...message);
  }

  public static info(context: string, ...message: any[]): void {
    Logger.log(context, Level.Info, ...message);
  }

  public static debug(context: string, ...message: any[]): void {
    Logger.log(context, Level.Debug, ...message);
  }

  public static trace(context?: string): void {
    if (!A11yConsentConfiguration.settings.settings.debug.enabled) {
      return;
    }
    console.trace(context ?? 'trace');
  }
}
